import React, { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, ShieldCheck, Check } from "lucide-react";
import { getChildSeatBySlug, CHILD_SEATS } from "../data/pricing";
import { BRAND } from "../mock";
import { useLang } from "../i18n/LanguageContext";
import { useSeo, SEO_BASE } from "../lib/seo";
import BookingWidget from "../components/BookingWidget";

const CONTENT = {
  maxicosi: {
    intro: [
      "Der Maxi-Cosi – auch Babyschale oder Gruppe-0+-Sitz genannt – ist die sicherste Wahl für Neugeborene und Säuglinge bis 13 kg (ca. 0–15 Monate). Bei Airport Taxi Zürich stellen wir Ihnen diese Babyschale kostenlos zur Verfügung, damit Ihr Baby bereits ab der Landung optimal geschützt reist.",
      "Die Babyschale wird stets entgegen der Fahrtrichtung montiert. Das ist bei Säuglingen besonders wichtig, weil so Kopf, Hals und Wirbelsäule bei einem Bremsmanöver optimal gestützt werden. Unsere Chauffeure montieren den Sitz fachgerecht und prüfen den korrekten Sitz vor jeder Fahrt.",
    ],
    points: ["Für 0–13 kg (Gruppe 0+)", "Rückwärts montiert für maximalen Schutz", "Kostenlos bei jeder Fahrt", "Fachgerechte Montage durch den Chauffeur"],
  },
  kindersitz: {
    intro: [
      "Der klassische Kindersitz (Gruppe 1) ist für Kleinkinder von 9 bis 18 kg konzipiert – ungefähr vom 9. Monat bis zum 4. Lebensjahr. Er bietet dem Kind durch ein Gurt- oder Fangkörpersystem sicheren Halt und ist bei Airport Taxi Zürich kostenlos erhältlich.",
      "Ein korrekt montierter Kindersitz reduziert das Verletzungsrisiko deutlich. Geben Sie bei der Buchung einfach an, dass Sie einen Kindersitz benötigen – wir sorgen dafür, dass er bei Ihrer Ankunft am Flughafen Zürich bereitsteht und richtig eingebaut ist.",
    ],
    points: ["Für 9–18 kg (Gruppe 1)", "Sicheres Gurt-/Fangkörpersystem", "Ideal für Kleinkinder von 9 Monaten bis 4 Jahren", "Kostenlos und fachgerecht montiert"],
  },
  sitzerhohung: {
    intro: [
      "Die Sitzerhöhung (Gruppe 2/3) ist für grössere Kinder von 15 bis 36 kg gedacht – etwa vom 4. bis zum 12. Lebensjahr. Sie hebt das Kind so an, dass der reguläre Sicherheitsgurt korrekt über Schulter und Becken verläuft.",
      "In der Schweiz sind Kinder bis 12 Jahre bzw. bis zu einer Körpergrösse von 150 cm mit einer geeigneten Rückhalteeinrichtung zu sichern. Unsere Sitzerhöhungen erfüllen diese Vorgaben und stehen Ihnen bei Airport Taxi Zürich kostenlos zur Verfügung.",
    ],
    points: ["Für 15–36 kg (Gruppe 2/3)", "Korrekter Gurtverlauf über Schulter und Becken", "Für Kinder von ca. 4 bis 12 Jahren", "Kostenlos auf Wunsch"],
  },
};

const ChildSeat = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t } = useLang();
  const seat = getChildSeatBySlug(slug);

  useSeo(seat ? {
    title: `${seat.name} im Airport Taxi Zürich | Kostenlose Kindersitze`,
    description: seat.short,
    path: `/kindersitze/${seat.slug}`,
    image: seat.img,
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        { "@type": "Product", name: seat.name, description: seat.short, image: seat.img, brand: { "@type": "Brand", name: BRAND.name }, offers: { "@type": "Offer", price: "0", priceCurrency: "CHF", availability: "https://schema.org/InStock" } },
        { "@type": "BreadcrumbList", itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: SEO_BASE + "/" },
          { "@type": "ListItem", position: 2, name: "Ratgeber", item: SEO_BASE + "/ratgeber" },
          { "@type": "ListItem", position: 3, name: seat.name, item: SEO_BASE + `/kindersitze/${seat.slug}` },
        ] },
      ],
    },
  } : {});

  useEffect(() => {
    window.scrollTo(0, 0);
    if (seat) document.title = `${seat.name} im Airport Taxi Zürich | Kostenlose Kindersitze`;
  }, [seat]);

  useEffect(() => { if (!seat) navigate("/ratgeber", { replace: true }); }, [seat, navigate]);
  if (!seat) return null;

  const c = CONTENT[seat.id];
  const others = CHILD_SEATS.filter((s) => s.id !== seat.id);

  return (
    <div className="pt-[68px] min-h-screen bg-white">
      <div className="bg-[#0B1533] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
          <Link to="/ratgeber" className="inline-flex items-center gap-2 font-body text-sm text-white/60 hover:text-white transition-colors mb-6"><ArrowLeft size={16} /> {t("blog.back")}</Link>
          <p className="font-body text-sm text-[#C9A24B] font-semibold mb-3">Kindersitze · {seat.subtitle}</p>
          <h1 className="font-heading font-bold tracking-tight text-3xl sm:text-5xl leading-[1.08]">{seat.name} im Airport Taxi Zürich</h1>
          <p className="font-body text-white/70 mt-4 text-lg max-w-2xl">{seat.short}</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-10 relative z-20">
        <BookingWidget defaultTo="" />
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid md:grid-cols-2 gap-10 items-start">
        <div className="rounded-3xl overflow-hidden border border-[#E9E5DE]">
          <img src={seat.img} alt={seat.name} className="w-full h-full object-cover" />
        </div>
        <div>
          {c.intro.map((p, i) => (<p key={i} className="font-body text-[#3d4257] leading-[1.8] mb-4">{p}</p>))}
          <ul className="space-y-2.5 mt-6">
            {c.points.map((pt, i) => (
              <li key={i} className="flex items-start gap-3 font-body text-[#0B1533]"><Check size={20} className="text-[#25D366] shrink-0 mt-0.5" /> {pt}</li>
            ))}
          </ul>
          <div className="flex items-center gap-2 mt-8 rounded-2xl bg-[#EEF1FF] p-4">
            <ShieldCheck size={22} className="text-[#2B4BFF] shrink-0" />
            <p className="font-body text-sm text-[#0B1533]">Alle Kindersitze entsprechen den geltenden Schweizer Sicherheitsvorschriften und sind im Fixpreis inklusive.</p>
          </div>
          <div className="flex flex-wrap gap-3 mt-8">
            <Link to={`/booking?seat=${seat.id}`} className="inline-flex items-center gap-2 bg-[#2B4BFF] text-white font-body font-semibold rounded-full px-7 py-3.5 hover:bg-[#1E38D8] transition-colors">{t("common.bookNow")} <ArrowRight size={16} /></Link>
            <a href={BRAND.whatsapp} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 border border-[#25D366] text-[#25D366] font-body font-semibold rounded-full px-7 py-3.5 hover:bg-[#25D366] hover:text-white transition-colors">WhatsApp</a>
          </div>
        </div>
      </div>

      <div className="bg-[#F5F2ED] border-t border-[#E9E5DE]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h2 className="font-heading font-semibold text-xl text-[#0B1533] mb-5">Weitere Kindersitze</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {others.map((s) => (
              <Link key={s.id} to={`/kindersitze/${s.slug}`} className="group flex items-center gap-4 rounded-2xl border border-[#E9E5DE] bg-white p-4 hover:border-[#2B4BFF] transition-colors">
                <img src={s.img} alt={s.name} className="w-16 h-16 rounded-xl object-cover" />
                <div><span className="font-heading font-semibold text-[#0B1533]">{s.name}</span><p className="font-body text-xs text-[#5A607A]">{s.subtitle}</p></div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChildSeat;
